import { el, clear } from "@utils/dom";
import { nid } from "@utils/id";
import { getState, setState, subscribe } from "@state/store";
import type { ProximityConstraint, ProximityType, Student } from "../../types/models";
import { openRadiusSelector } from "./RadiusSelector";
import { errorToast } from "./Toasts";

/**
 * Show a confirm dialog and return true/false
 */
function showConfirmDialog(message: string, confirmText = "Remove"): Promise<boolean> {
    return new Promise((resolve) => {
        const backdrop = el("div", {
            style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
        });

        const dialog = el("div", {
            style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 400px; width: 90%; border: 1px solid var(--border);"
        });

        const label = el("p", { style: "margin: 0 0 16px 0; font-weight: 500;" }, message);

        const buttonRow = el("div", { style: "display: flex; gap: 8px; justify-content: flex-end;" });

        const cancelBtn = el("button", { style: "padding: 8px 16px;" }, "Cancel");
        const confirmBtn = el("button", { class: "danger", style: "padding: 8px 16px;" }, confirmText);

        const close = (result: boolean) => {
            backdrop.remove();
            resolve(result);
        };

        cancelBtn.addEventListener("click", () => close(false));
        confirmBtn.addEventListener("click", () => close(true));

        backdrop.addEventListener("keydown", (e) => {
            if (e.key === "Escape") close(false);
            if (e.key === "Enter") close(true);
        });

        backdrop.addEventListener("click", (e) => {
            if (e.target === backdrop) close(false);
        });

        buttonRow.append(cancelBtn, confirmBtn);
        dialog.append(label, buttonRow);
        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        confirmBtn.focus();
    });
}

/**
 * Create the proximity constraint editor component
 */
export function createProximityEditor(): HTMLElement {
    const container = el("div", { class: "proximity-editor" });

    function render() {
        clear(container);

        const s = getState();
        const cls = s.classes.find(c => c.id === s.activeClassId);
        if (!cls) return;

        const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId)
            ?? cls.seatingCharts?.[0];

        if (!chart) {
            container.appendChild(
                el("p", { style: "color: var(--muted); text-align: center; padding: 1rem;" },
                    "Create a seating chart to add proximity constraints"
                )
            );
            return;
        }

        // Privacy mode toggle
        const privacyRow = el("div", { class: "privacy-toggle-row" });
        const privacyLabel = el("label", { class: "toggle-label" },
            el("span", {}, "Privacy Mode"),
            el("span", { class: "toggle-hint" }, "Blur names for screen sharing")
        );
        const toggleSwitch = el("label", { class: "toggle-switch" });
        const blurCheckbox = el("input", { type: "checkbox" }) as HTMLInputElement;
        blurCheckbox.checked = cls.settings.constraintsBlurred ?? false;
        const slider = el("span", { class: "toggle-slider" });
        toggleSwitch.append(blurCheckbox, slider);
        privacyRow.append(privacyLabel, toggleSwitch);
        container.appendChild(privacyRow);

        const isBlurred = cls.settings.constraintsBlurred ?? false;

        // Only show teacher constraints (hide admin constraints)
        const teacherConstraints = chart.proximityConstraints.filter(c => c.source !== 'admin');

        // Within constraints section
        const withinSection = renderSection(
            "Keep Within",
            "Students who should sit close together",
            teacherConstraints.filter(c => c.type === 'within'),
            'within',
            cls.students,
            chart.id,
            isBlurred
        );

        // Apart constraints section
        const apartSection = renderSection(
            "Keep Apart",
            "Students who should sit far apart",
            teacherConstraints.filter(c => c.type === 'apart'),
            'apart',
            cls.students,
            chart.id,
            isBlurred
        );

        container.append(withinSection, apartSection);

        // Handle blur toggle
        blurCheckbox.addEventListener("change", () => {
            const newBlurred = blurCheckbox.checked;
            setState(ns => {
                const c = ns.classes.find(x => x.id === ns.activeClassId);
                if (c) {
                    c.settings.constraintsBlurred = newBlurred;
                }
            });
            // Update sections immediately
            [withinSection, apartSection].forEach(section => {
                const list = section.querySelector('.proximity-list');
                if (list) {
                    if (newBlurred) {
                        list.classList.add('constraints-blurred');
                    } else {
                        list.classList.remove('constraints-blurred');
                    }
                }
            });
        });
    }

    function renderSection(
        title: string,
        subtitle: string,
        constraints: ProximityConstraint[],
        type: ProximityType,
        students: Student[],
        chartId: string,
        isBlurred: boolean
    ): HTMLElement {
        const section = el("div", { class: "proximity-section" });

        // Header
        const header = el("div", { class: "proximity-section-header" });

        const titleEl = el("div", {},
            el("h4", { style: "margin: 0;" }, title),
            el("p", { style: "margin: 0; font-size: 0.75rem; color: var(--muted);" }, subtitle)
        );

        const addBtn = el("button", {}, "+ Add");
        addBtn.addEventListener("click", () => openVisualRadiusSelector(type, students, chartId, render));

        header.append(titleEl, addBtn);
        section.appendChild(header);

        // List
        const list = el("div", { class: "proximity-list" });
        if (isBlurred) {
            list.classList.add('constraints-blurred');
        }

        if (constraints.length === 0) {
            list.appendChild(
                el("p", { class: "proximity-empty" }, "No constraints")
            );
        } else {
            for (const constraint of constraints) {
                list.appendChild(renderConstraintItem(constraint, students, chartId, type, render));
            }
        }

        section.appendChild(list);
        return section;
    }

    function renderConstraintItem(
        constraint: ProximityConstraint,
        students: Student[],
        chartId: string,
        type: ProximityType,
        onUpdate: () => void
    ): HTMLElement {
        const item = el("div", { class: "proximity-item" });

        const info = el("div", { class: "proximity-item-info" });

        // Icon
        const icon = el("span", {
            class: `proximity-icon ${type}`
        }, type === 'within' ? '↔' : '↕');

        // Names
        const names = constraint.studentIds
            .map(id => students.find(s => s.id === id)?.name ?? id)
            .join(' & ');

        const namesEl = el("span", {}, names);

        // Radius badge (pixel-based)
        const radiusBadge = el("span", { class: "proximity-radius" },
            `${constraint.radius}px`
        );

        info.append(icon, namesEl, radiusBadge);

        // Delete button
        const deleteBtn = el("button", {
            class: "proximity-delete-btn",
            onclick: async () => {
                const confirmed = await showConfirmDialog("Remove this constraint?");
                if (!confirmed) return;

                setState(ns => {
                    const c = ns.classes.find(x => x.id === ns.activeClassId);
                    const ch = c?.seatingCharts?.find(x => x.id === chartId);
                    if (ch) {
                        ch.proximityConstraints = ch.proximityConstraints.filter(
                            x => x.id !== constraint.id
                        );
                    }
                });

                onUpdate();
            }
        }, "×");

        item.append(info, deleteBtn);
        return item;
    }

    // Initial render
    render();

    // Subscribe to updates
    subscribe(render);
    document.addEventListener("seating:update", render);

    return container;
}

/**
 * Open visual radius selector, then student selector
 */
function openVisualRadiusSelector(
    type: ProximityType,
    students: Student[],
    chartId: string,
    onComplete: () => void
): void {
    openRadiusSelector(type, (result) => {
        if (!result) return;

        // Now open student selector with the radius already set
        openStudentSelector(type, students, chartId, result.radius, onComplete);
    });
}

/**
 * Open modal to select students for the constraint
 */
function openStudentSelector(
    type: ProximityType,
    students: Student[],
    chartId: string,
    radius: number,
    onComplete: () => void
): void {
    const presentStudents = students.filter(s => !s.absent);

    if (presentStudents.length < 2) {
        errorToast("Need at least 2 students to create a constraint");
        return;
    }

    // Create modal backdrop
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    // Create dialog
    const dialog = el("div", {
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 400px; width: 90%; border: 1px solid var(--border);"
    });

    const title = el("h3", { style: "margin: 0 0 16px 0;" },
        type === 'within' ? "Select Students to Keep Within" : "Select Students to Keep Apart"
    );

    const radiusInfo = el("div", {
        style: "margin-bottom: 16px; padding: 12px; background: var(--bg); border-radius: 8px; text-align: center;"
    },
        el("span", { style: "font-size: 0.875rem; color: var(--muted);" }, "Radius: "),
        el("span", { style: "font-weight: 600;" }, `${radius}px`)
    );

    // Student 1 select
    const student1Label = el("label", {
        style: "display: block; margin-bottom: 8px; font-weight: 500;"
    }, "Student 1");

    const student1Select = el("select", {
        style: "width: 100%; padding: 8px; margin-bottom: 16px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg);"
    }) as HTMLSelectElement;

    for (const student of presentStudents) {
        student1Select.appendChild(el("option", { value: student.id }, student.name));
    }

    // Student 2 select
    const student2Label = el("label", {
        style: "display: block; margin-bottom: 8px; font-weight: 500;"
    }, "Student 2");

    const student2Select = el("select", {
        style: "width: 100%; padding: 8px; margin-bottom: 16px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg);"
    }) as HTMLSelectElement;

    for (const student of presentStudents) {
        student2Select.appendChild(el("option", { value: student.id }, student.name));
    }

    // Default to second student
    const secondStudent = presentStudents[1];
    if (presentStudents.length > 1 && secondStudent) {
        student2Select.value = secondStudent.id;
    }

    // Buttons
    const buttons = el("div", {
        style: "display: flex; gap: 8px; justify-content: flex-end;"
    });

    const cancelBtn = el("button", {
        style: "padding: 8px 16px;"
    }, "Cancel");

    cancelBtn.addEventListener("click", () => {
        document.body.removeChild(backdrop);
    });

    const addBtn = el("button", {
        class: "primary",
        style: "padding: 8px 16px;"
    }, "Add Constraint");

    addBtn.addEventListener("click", () => {
        const studentId1 = student1Select.value;
        const studentId2 = student2Select.value;

        if (studentId1 === studentId2) {
            errorToast("Please select two different students");
            return;
        }

        const constraint: ProximityConstraint = {
            id: nid("prox_"),
            studentIds: [studentId1, studentId2],
            type,
            radius,
            source: 'teacher'
        };

        setState(ns => {
            const c = ns.classes.find(x => x.id === ns.activeClassId);
            const ch = c?.seatingCharts?.find(x => x.id === chartId);
            if (ch) {
                // Remove any admin constraints for the same students (teacher overrides admin)
                ch.proximityConstraints = ch.proximityConstraints.filter(pc =>
                    !(pc.source === 'admin' &&
                      pc.studentIds.includes(studentId1) &&
                      pc.studentIds.includes(studentId2))
                );
                ch.proximityConstraints.push(constraint);
            }
        });

        document.body.removeChild(backdrop);
        onComplete();
    });

    buttons.append(cancelBtn, addBtn);

    dialog.append(
        title,
        radiusInfo,
        student1Label,
        student1Select,
        student2Label,
        student2Select,
        buttons
    );

    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) {
            document.body.removeChild(backdrop);
        }
    });
}

/**
 * Open standalone proximity editor dialog
 */
export function openProximityEditorDialog(): void {
    const s = getState();
    const cls = s.classes.find(c => c.id === s.activeClassId);
    if (!cls) return;

    const chart = cls.seatingCharts?.find(c => c.id === cls.activeChartId)
        ?? cls.seatingCharts?.[0];

    if (!chart) {
        errorToast("Create a seating chart first");
        return;
    }

    // Create modal backdrop
    const backdrop = el("div", {
        style: "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center;"
    });

    // Create dialog
    const dialog = el("div", {
        style: "background: var(--card); border-radius: 16px; padding: 24px; max-width: 500px; width: 90%; max-height: 80vh; overflow: auto; border: 1px solid var(--border);"
    });

    const title = el("h2", { style: "margin: 0 0 16px 0;" }, "Proximity Constraints");

    const editor = createProximityEditor();

    const closeBtn = el("button", {
        class: "primary",
        style: "width: 100%; margin-top: 16px;"
    }, "Done");

    closeBtn.addEventListener("click", () => {
        document.body.removeChild(backdrop);
    });

    dialog.append(title, editor, closeBtn);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) {
            document.body.removeChild(backdrop);
        }
    });
}
